// @flow
import React, { PureComponent, Fragment } from 'react';
import { Link } from 'react-router-dom';

import { withHelmet } from 'infrastructure/seo/Helmet';
import Breadcrumb from 'components/fragments/Breadcrumb';
import CardLoge from 'containers/fragments/card/CardLogeContainer';
import DropdownGuest from 'components/fragments/input/DropdownGuest';
import DropdownMatch from 'components/fragments/input/DropdownMatch';
import PageNotFound from 'components/app/PageNotFound';
import Loading from 'components/fragments/Loading';
import FooterArticle from 'components/newsletter/FooterArticle';
import { STATUS_LOADING, STATUS_DEFAULT, STATUS_UNKNOWN, STATUS_SUCCESS } from 'constants/statusConstants';
import { getEntrepriseBreadcrumb } from 'utils/urlUtils';
import { sendTags } from 'utils/tagManagerUtils';

import type { LogeListType } from 'types/LogeList';
import type { Status } from 'types/Status';
import type { EntrepriseBreadcrumb } from 'types/Settings';
import type { ArticleType } from 'types/Article';

type OwnProps = {
  match: {
    params: {
      matchSlug: string,
    }
  }
};

export type DispatchProps = {
  fetchLogeList: (match?: string) => void,
  fetchArticles: (category: any) => void,
  fetchPageDetail: (slug: string) => void,
};

export type StateProps = {
  loges: Array<LogeListType>,
  title: string,
  description: string,
  status: Status,
  breadcrumb_offres_entreprise: Array<EntrepriseBreadcrumb>,
  slug_article: Object,
  articles: Array<ArticleType>,
};

type Props = DispatchProps & StateProps & OwnProps;

type State = {
  searchedLoges: Array<LogeListType> | null,
  peopleInput: number,
  rencontreFilter: number | null,
  rencontreTitle: string,
};

class LogeList extends PureComponent<Props, State> {
  state: State = {
    loges: null,
    searchedLoges: null,
    peopleInput: 0,
    rencontreFilter: null,
    rencontreTitle: '',
  };

  descriptionRef = React.createRef();

  componentDidMount() {
    const {
      fetchLogeList,
      fetchPageDetail,
      match: { params: { matchSlug } },
      breadcrumb_offres_entreprise,
    } = this.props;

    fetchLogeList(matchSlug);


    if (breadcrumb_offres_entreprise.length > 0) {
      fetchPageDetail(breadcrumb_offres_entreprise[breadcrumb_offres_entreprise.length - 1].slug);
    }
  }

  static getDerivedStateFromProps(props, state) {
    const { loges, status } = props;
    const { loges: logesState } = state;

    if (
      status === STATUS_SUCCESS &&
      loges.length > 0 &&
      (logesState === null || (logesState && logesState.length !== loges.length))
    ) {
      return { searchedLoges: loges, loges };
    }

    return null;
  }

  componentDidUpdate(prevProps: Props) {
    const { slug_article, fetchArticles, articles } = this.props;

    if (
      !prevProps.slug_article.id &&
      !!slug_article.id &&
      prevProps.articles.length === 0 &&
      articles.length === 0
    ) {
      fetchArticles(slug_article);
    }
  }

  _onRencontreFilterChange = (value: number) => {
    this.setState({ rencontreFilter: value });
  }

  _handleSubmit = (event: Event) => {
    event.preventDefault();
    const { loges } = this.props;
    const { peopleInput, rencontreFilter, rencontreTitle } = this.state;

    const searchedLoges = loges.filter((loge) => {
      let rencontre = true;
      if (rencontreFilter) {
        rencontre = loge.rencontres.find((rencontre) => rencontre.id === rencontreFilter);
      }

      if (loge.nombreDePlace === 0) {
        return rencontre;
      }
      return rencontre && loge.nombreDePlace >= peopleInput;
    });
    sendTags('recherche_offre_match', rencontreTitle);
    this.setState({ searchedLoges }, () => this._scrollToList());
  }

  _scrollToList = () => {
    if (this.descriptionRef && typeof this.descriptionRef.scrollIntoView === 'function') {
      this.descriptionRef.scrollIntoView(true);
    } else {
      window.scrollTo(0, 700);
    }
  }

  _renderForm = () => {
    const { loges } = this.props;
    const { peopleInput, rencontreFilter } = this.state;

    return (
      <form className="form-match mb-5 mb-md-7 mb-lg-8">
        <DropdownMatch
          id={'match'}
          label={'Séléctionner un match'}
          headTitle={'Match'}
          loges={loges}
          inputChanged={(value: number, label?: string) => this.setState(
            { rencontreFilter: value, rencontreTitle: label }
          )}
          big
          />
        <DropdownGuest
          label={'Invité'}
          headTitle={'Nombre d\'invité'}
          initialValue={peopleInput}
          inputChanged={(value: number) => this.setState({ peopleInput: value })}
          big
          />
        <button
          type="submit"
          disabled={peopleInput === 0 || rencontreFilter === null}
          className="mt-2 mt-md-0 btn btn--primary btn--big btn--full"
          onClick={this._handleSubmit}>
          <i className="icon icon-search is-inline"></i>
          Rechercher une offre
        </button>
      </form>
    );
  }

  _renderDescriptionBloc = () => (
    <div className="card card--exp mb-2 mb-lg-3" ref={(el) => this.descriptionRef = el}>
      <i className="icon icon-exp"></i>
      <p>
        <strong>Personnalisez votre expérience. </strong>
        Quelle que soit l’offre qui vous intéresse, les possibilités de personnalisation sont infinies. 
        Ne vous arrêtez pas aux prestations listées et n’hésitez pas à{' '}
        <Link
          to={{
            pathname: '/contact/nous-contacter',
            state: { object: `Demande d'offre hospitalité`, isPopulate: true, isOffer: true }
          }}>
          nous contacter
        </Link>
        {' '}pour concocter ensemble la meilleure expérience.
      </p>
    </div>
  );

  _renderList = () => {
    const { searchedLoges } = this.state;

    if (searchedLoges) {
      return searchedLoges.sort((logeA, logeB) => logeA.order - logeB.order).map((loge) => {
        const {
          title,
          nombreDePlace,
          position,
          exclusivite,
          cadeau,
          link,
          prix_base,
          placeDeParking,
          logeDescription,
          repas,
          slug,
          featured_media: { src },
        } = loge;

        return (
          <CardLoge
            key={slug}
            link={link}
            title={title}
            nombreDePlace={nombreDePlace}
            exclusivite={exclusivite}
            position={position}
            cadeau={cadeau}
            prix={prix_base}
            placeDeParking={placeDeParking}
            repas={repas}
            image={src}
            logeDescription={logeDescription}
          />
        );
      });
    }

    return null;
  }

  _getEntrepriseBreadcrumb = () => {
    const { breadcrumb_offres_entreprise } = this.props;

    return getEntrepriseBreadcrumb(breadcrumb_offres_entreprise);
  }

  _renderArticles = () => {
    const { articles, slug_article } = this.props;

    return <FooterArticle articles={articles} slug={slug_article.slug} label={slug_article.name} />;
  }

  render() {
    const { title, description, status, articles, slug_article, match: { params: { matchSlug } } } = this.props;
    const disabledSearch = matchSlug === "paris-sevens" || matchSlug === "offre-du-moment";

    if (status === STATUS_LOADING || status === STATUS_DEFAULT) {
      return <Loading isBlue backgroundColor={'#013974'} />;
    }

    if (status === STATUS_UNKNOWN) {
      return <PageNotFound noMargin />;
    }

    const elementBreadcrumb = this._getEntrepriseBreadcrumb();

    return (
      <Fragment>
        <section className="banner banner--enterprise">
          <div
            className="banner__thumbnail img-blue-dark"
            style={{ backgroundImage: "url('/img/banner/loges.jpg')"}}/>
          <div className="container">
            <Breadcrumb
              elements={[
                { name: 'accueil', to: '/' },
                ...elementBreadcrumb,
                title
              ]}
              isDark
            />
            <h1 className="ft-h2">{title}</h1>
            <p className="banner__desc">{description}</p>
          </div>
        </section>
        <div className="container mb-5 mb-md-7 mb-lg-8">
          {!disabledSearch && this._renderForm()}
          {disabledSearch && <form className="form-match mb-5 mb-md-7 mb-lg-8" />}
          <div className='row'>
            <div className='col'>
              <h2 className='mb-4'>Découvrez tous nos lieux</h2>
              {this._renderDescriptionBloc()}
              {this._renderList()}
              {articles.length > 0 && slug_article.id && this._renderArticles()}
            </div>
          </div>
        </div>
      </Fragment>
    );
  }
}
export default withHelmet(LogeList)((Helmet, pageProps) => {
  const { meta_title, meta_description } = pageProps;

  return (
    <Helmet>
      <title>{meta_title}</title>
      <meta name={'description'} content={meta_description} />
      <meta property={'og:title'} content={meta_title} />
      <meta property={'og:description'} content={meta_description} />
      <link rel={'canonical'} href={window.location.href} />
    </Helmet>
  );
});
